// utils/orgOptions.js
//
// Unit-wise Department / Designation / Reporting Manager option helpers
// shared by the Admin staff form (pages/admins/AdminStaffPage.js) and the
// Manager Team Users form (pages/manager/ManagerTeamUsersPage.jsx).
// UX only -- the backend validates every submitted value.

// Unit-agnostic rows (unit_id null) belong to every unit. With no unit
// selected, everything matches.
export const belongsToUnit = (unitId, selectedUnitId) =>
  !selectedUnitId || unitId == null || unitId === "" || String(unitId) === String(selectedUnitId);

const sameId = (a, b) => a != null && a !== "" && String(a) === String(b);

// Departments of the unit plus global ones.
export const filterDepartmentsByUnit = (departments, unitId) =>
  departments.filter((d) => belongsToUnit(d.unit_id, unitId));

// With a department picked: its own designations plus department-agnostic
// ones. Without one: designations whose department is in
// `allowedDepartmentIds` (the unit's departments) plus department-agnostic
// ones; pass null to skip that narrowing.
export const filterDesignations = (designations, departmentId, allowedDepartmentIds = null) =>
  designations.filter((d) => {
    if (d.department_id == null || d.department_id === "") return true;
    if (departmentId) return sameId(d.department_id, departmentId);
    return !allowedDepartmentIds || allowedDepartmentIds.has(String(d.department_id));
  });

// Active staff of the unit, never the user being edited.
export const filterReportingManagers = (users, { unitId, excludeId } = {}) =>
  users.filter(
    (u) =>
      !sameId(u.id, excludeId) &&
      (u.is_active === undefined || !!Number(u.is_active)) &&
      (!unitId || sameId(u.unit_id, unitId))
  );

// Keeps a saved value renderable when it is not among the selectable
// options (old data pointing at another unit, or an inactive record): it is
// appended as a `saved` entry the form renders disabled, so it shows as the
// current value but cannot be picked again once changed.
export const withSavedOption = (options, savedId, savedName) => {
  if (!savedId || options.some((o) => sameId(o.id, savedId))) return options;
  return [...options, { id: savedId, name: savedName || `#${savedId}`, saved: true }];
};

// The departments/designations list APIs cap pageSize at 200; walk every
// page so a dropdown never silently misses rows.
export async function fetchAllPages(api, url, params = {}, pageSize = 200) {
  let page = 1;
  let rows = [];
  for (;;) {
    const res = await api.get(url, { params: { ...params, page, pageSize } });
    const batch = Array.isArray(res.data?.data) ? res.data.data : [];
    rows = rows.concat(batch);
    const total = Number(res.data?.meta?.total) || 0;
    if (batch.length < pageSize || rows.length >= total) break;
    page += 1;
  }
  return rows;
}
